import React, { useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, Divider, IconButton,
  MenuItem, Paper, TextField, Tooltip, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { EmailAccount, EmailMsg, EmailHubTemplate, sendEmail, fetchTemplates } from "./emailHubApi";

const T = {
  navy: "#0d1b2a", gold: "#c9a96e", goldLight: "#f5eed9",
  white: "#fff", cream: "#faf8f4", bg: "#f4f2ee",
  slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
  blue: "#1e40af", blueBg: "#eff6ff",
};

interface Props {
  open: boolean; onClose: () => void; accounts: EmailAccount[];
  replyTo?: EmailMsg | null; threadId?: number | null;
  initialBody?: string; onSent: () => void;
}

const ComposeModal: React.FC<Props> = ({ open, onClose, accounts, replyTo, threadId, initialBody, onSent }) => {
  const [accountId, setAccountId] = useState<number | "">("");
  const [to, setTo] = useState(""); const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(""); const [body, setBody] = useState("");
  const [sending, setSending] = useState(false); const [showCc, setShowCc] = useState(false);
  const [templates, setTemplates] = useState<EmailHubTemplate[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);

  useEffect(() => {
    if (open) {
      fetchTemplates().then(setTemplates).catch(() => {});
      if (replyTo) {
        setTo(replyTo.from_email || "");
        setSubject(replyTo.subject.startsWith("Re:") ? replyTo.subject : `Re: ${replyTo.subject}`);
        setBody(initialBody || "");
        setAccountId(replyTo.account_id || (accounts[0]?.id ?? ""));
      } else {
        setTo(""); setSubject(""); setBody(initialBody || "");
        setAccountId(accounts[0]?.id ?? "");
      }
      setCc(""); setShowCc(false); setShowTemplates(false);
    }
  }, [open, replyTo, accounts, initialBody]);

  const applyTemplate = (tpl: EmailHubTemplate) => {
    setSubject(tpl.subject);
    setBody(tpl.body);
    setShowTemplates(false);
  };

  const handleSend = async () => {
    if (!accountId || !to.trim() || !subject.trim() || !body.trim()) return;
    setSending(true);
    try {
      await sendEmail({
        account_id: accountId as number,
        to: to.split(",").map(e => e.trim()).filter(Boolean),
        cc: cc ? cc.split(",").map(e => e.trim()).filter(Boolean) : undefined,
        subject, body: body.replace(/\n/g, "<br>"),
        thread_id: threadId || undefined,
      });
      onSent();
    } catch (e) { console.error("Send failed", e); }
    setSending(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", maxHeight: "85vh" } }}>
      <Box sx={{ px: 2.5, py: 1.5, bgcolor: T.navy, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.white }}>{replyTo ? "Reply" : "New Message"}</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>

      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* From */}
        <TextField select size="small" label="From" value={accountId} onChange={e => setAccountId(Number(e.target.value))}
          sx={{ "& .MuiInputBase-root": { fontSize: 13, borderRadius: 2 } }}>
          {accounts.map(a => <MenuItem key={a.id} value={a.id}><Chip label={a.email.split("@")[0]} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: T.blueBg, color: T.blue, mr: 1 }} />{a.email}</MenuItem>)}
        </TextField>

        {/* To + Cc */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField fullWidth size="small" label="To" value={to} onChange={e => setTo(e.target.value)} placeholder="email@example.com"
            sx={{ "& .MuiInputBase-root": { fontSize: 13, borderRadius: 2 } }} />
          {!showCc && <Button size="small" onClick={() => setShowCc(true)} sx={{ textTransform: "none", fontSize: 11, color: T.slateLt, whiteSpace: "nowrap" }}>Cc</Button>}
        </Box>
        {showCc && <TextField fullWidth size="small" label="Cc" value={cc} onChange={e => setCc(e.target.value)} sx={{ "& .MuiInputBase-root": { fontSize: 13, borderRadius: 2 } }} />}

        {/* Subject */}
        <TextField fullWidth size="small" label="Subject" value={subject} onChange={e => setSubject(e.target.value)}
          sx={{ "& .MuiInputBase-root": { fontSize: 13, borderRadius: 2 } }} />

        {/* Template picker */}
        {showTemplates && templates.length > 0 && (
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, maxHeight: 160, overflow: "auto" }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", mb: 1 }}>Templates</Typography>
            {templates.map(tpl => (
              <Box key={tpl.id} onClick={() => applyTemplate(tpl)} sx={{
                p: 1, borderRadius: 1.5, cursor: "pointer", mb: 0.5,
                "&:hover": { bgcolor: T.goldLight },
              }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy }}>{tpl.name}</Typography>
                <Typography noWrap sx={{ fontSize: 10, color: T.slateLt }}>{tpl.subject}</Typography>
              </Box>
            ))}
          </Paper>
        )}

        {/* Body */}
        <TextField fullWidth multiline rows={10} value={body} onChange={e => setBody(e.target.value)} placeholder="Write your message..."
          sx={{ "& .MuiInputBase-root": { fontSize: 13, borderRadius: 2, lineHeight: 1.7 }, "& fieldset": { borderColor: T.line } }} />

        {replyTo && (
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: T.bg, border: `1px solid ${T.line}` }}>
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.slateLt, mb: 0.5 }}>Replying to {replyTo.from_name || replyTo.from_email}</Typography>
            <Typography sx={{ fontSize: 11, color: T.slate, lineHeight: 1.5 }} noWrap>{replyTo.body_preview}</Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${T.line}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Templates"><IconButton size="small" onClick={() => setShowTemplates(p => !p)} sx={{ color: showTemplates ? T.gold : T.slateLt }}>
            <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton></Tooltip>
          <Tooltip title="Attach file (coming soon)"><IconButton size="small" disabled sx={{ color: T.slateLt }}>
            <AttachFileIcon sx={{ fontSize: 18 }} />
          </IconButton></Tooltip>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 12, color: T.slate }}>Discard</Button>
          <Button variant="contained" onClick={handleSend} disabled={sending || !to.trim() || !subject.trim()}
            startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon sx={{ fontSize: 14 }} />}
            sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, bgcolor: T.navy, borderRadius: 2, px: 3, "&:hover": { bgcolor: T.gold, color: T.navy } }}>
            {sending ? "Sending..." : "Send"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ComposeModal;
